"use client";

import { useEffect, useRef, useState } from "react";
import { normalizeIsbn, formatIsbn } from "@/lib/domain/isbn";
import type { IScannerControls } from "@zxing/browser";

/**
 * Format-as-you-type ISBN input: the value the parent holds is always
 * digits-only (what's stored/searched), this just displays it hyphenated
 * and re-normalizes whatever the user types or pastes — so pasting a
 *13-digit scanned code fills in the hyphens, and pasting an already
 * hyphenated one is a no-op past the first render.
 *
 * The scan button prefers a live camera preview (continuous decode via
 * @zxing/browser + getUserMedia) — this is what lets you just "show" the
 * barcode to a phone or a desktop webcam. getUserMedia only works in a
 * secure context (HTTPS, or localhost) though: the app is also reachable
 * over plain HTTP directly on the school LAN IP (see docker-compose.prod.yml)
 * where no browser allows camera access at all. When getUserMedia isn't
 * available we fall back to a plain file input with `capture="environment"`
 * (opens the device camera app for a single photo, no secure-context
 * requirement) — worse UX (one photo instead of point-and-scan) but it
 * still works. @zxing/browser is loaded on demand either way, so pages that
 * never touch this button don't pay for it.
 */
export function IsbnField({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (digitsOnly: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);

  const canUseLiveCamera =
    typeof window !== "undefined" && window.isSecureContext && Boolean(navigator.mediaDevices?.getUserMedia);

  function stopLiveCamera() {
    controlsRef.current?.stop();
    controlsRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLiveOpen(false);
  }

  // Belt-and-braces: release the camera if the component unmounts while a scan is open.
  useEffect(() => stopLiveCamera, []);

  async function startLiveCamera() {
    setScanError(false);
    setLiveOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error("no video element");
      video.srcObject = stream;
      await video.play();

      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      controlsRef.current = await new BrowserMultiFormatReader().decodeFromVideoElement(video, (result) => {
        if (result) {
          onChange(normalizeIsbn(result.getText()));
          stopLiveCamera();
        }
      });
    } catch {
      stopLiveCamera();
      setScanError(true);
    }
  }

  async function handleFile(file: File) {
    setScanning(true);
    setScanError(false);
    const url = URL.createObjectURL(file);
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const result = await new BrowserMultiFormatReader().decodeFromImageUrl(url);
      onChange(normalizeIsbn(result.getText()));
    } catch {
      setScanError(true);
    } finally {
      URL.revokeObjectURL(url);
      setScanning(false);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={formatIsbn(value)}
          onChange={(e) => onChange(normalizeIsbn(e.target.value))}
          placeholder={placeholder ?? "978-606-009-601-6"}
          className={
            className ??
            "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          }
        />
        <button
          type="button"
          onClick={() => (canUseLiveCamera ? startLiveCamera() : fileInputRef.current?.click())}
          disabled={scanning}
          title="Scanează codul de bare"
          aria-label="Scanează codul de bare"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 text-base hover:bg-slate-50 disabled:opacity-50"
        >
          {scanning ? "…" : "📷"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void handleFile(file);
          }}
        />
      </div>
      {scanError ? <p className="mt-1 text-xs text-red-600">Cod de bare nedetectat — încearcă din nou.</p> : null}

      {liveOpen ? (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 p-4">
          <video ref={videoRef} playsInline muted className="max-h-[70vh] w-full max-w-md rounded-lg bg-black" />
          <p className="mt-4 text-sm text-white">Îndreaptă camera spre codul de bare de pe manual.</p>
          <button
            type="button"
            onClick={stopLiveCamera}
            className="mt-4 rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
          >
            Anulează
          </button>
        </div>
      ) : null}
    </div>
  );
}
